<template>
  <div
    ref="wrapRef"
    class="buyer-country-bars flex min-h-0 h-full min-w-0 w-full flex-col items-stretch justify-start rounded-2xl border border-gray-modern-100 bg-white p-6 shadow-sm"
    role="region"
    :aria-label="ariaLabel"
  >
    <div class="flex items-start justify-between gap-3 shrink-0">
      <div class="min-w-0 flex-1">
        <h3 class="buyer-country-bars__title">{{ title }}</h3>
        <p class="buyer-country-bars__subtitle">{{ subtitle }}</p>
      </div>
      <ChartImageDownloadButton
        v-if="downloadable"
        :disabled="!canDownload"
        :busy="isDownloading"
        @click="downloadImage"
      />
    </div>

    <div
      ref="chartRef"
      class="buyer-country-bars__chart mt-6 min-h-0 w-full shrink-0"
    >
      <svg
        class="buyer-country-bars__svg"
        :viewBox="`0 0 ${svgW} ${svgH}`"
        preserveAspectRatio="xMinYMin meet"
        aria-hidden="true"
      >
        <g v-for="(row, i) in layout.rows" :key="i">
          <text
            :x="row.labelX"
            :y="row.cy"
            text-anchor="middle"
            dominant-baseline="middle"
            class="buyer-country-bars__row-label"
          >
            {{ row.label }}
          </text>
          <path
            v-if="row.count > 0 && row.barPath"
            :d="row.barPath"
            :fill="barColor"
          />
          <line
            v-else
            :x1="row.barX"
            :y1="row.cy"
            :x2="row.barX + minBarWidth"
            :y2="row.cy"
            stroke="var(--gray-modern-200, #E3E8EF)"
            stroke-width="8"
            stroke-linecap="round"
          />
          <text
            :x="row.valueX"
            :y="row.cy"
            text-anchor="start"
            dominant-baseline="middle"
            class="buyer-country-bars__value"
          >
            {{ formatCount(row.count) }}
          </text>
        </g>
      </svg>
    </div>
    <div class="min-h-0 flex-1" aria-hidden="true" />
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import ChartImageDownloadButton from "./ChartImageDownloadButton.vue";
import { useChartImageDownload } from "../utils/useChartImageDownload";

const barColor = "var(--Brand-500, #347774)";
const minBarWidth = 24;

/** Horizontal bar: square left edge, rounded caps on the right (design). */
function horizontalBarPathRightRound(x, y, w, h, cornerR) {
  if (!(w > 0) || !(h > 0)) return "";
  const r = Math.min(cornerR, h / 2, w / 2);
  if (r < 0.5) {
    return `M ${x} ${y} L ${x + w} ${y} L ${x + w} ${y + h} L ${x} ${y + h} Z`;
  }
  return [
    `M ${x} ${y}`,
    `L ${x + w - r} ${y}`,
    `A ${r} ${r} 0 0 1 ${x + w} ${y + r}`,
    `L ${x + w} ${y + h - r}`,
    `A ${r} ${r} 0 0 1 ${x + w - r} ${y + h}`,
    `L ${x} ${y + h}`,
    "Z",
  ].join(" ");
}

const MOCK_ROWS = [
  { label: "Brazil", count: 3546 },
  { label: "Ecuador", count: 2955 },
  { label: "Colombia", count: 2484 },
  { label: "Peru", count: 2400 },
  { label: "Chile", count: 900 },
  { label: "Argentina", count: 396 },
];

const props = defineProps({
  title: {
    type: String,
    default: "Number Workers by Country",
  },
  subtitle: {
    type: String,
    default: "Workers grouped by which country their facility is in",
  },
  /** JSON string: { label: string, count: number }[] */
  rowsJson: {
    type: String,
    default: "",
  },
  /** { label: string, count: number }[] */
  rows: {
    type: Array,
    default: undefined,
  },
  downloadable: { type: Boolean, default: false },
  downloadContext: { type: String, default: "" },
});

const wrapRef = ref(null);
const chartRef = ref(null);
const measuredW = ref(280);

const margin = { top: 4, right: 8, bottom: 8, left: 0 };
/** ~px per character for row labels (11px Open Sans); reserves space before bars. */
const LABEL_CHAR_PX = 6.75;
/** Extra px after estimated longest label width before gap to bars (0 → gap is exactly gapLabelToBar). */
const labelColumnPaddingPx = 0;
/** Row pitch — bar height is ~65% of this so bars read “chunky” like the design. */
const rowHeight = 48;
const barFillRatio = 0.65;
const barHeight = Math.round(rowHeight * barFillRatio);
const barRightCornerRadius = 5;
const gapLabelToBar = 6;
const gapBarToValue = 10;
/** ~px per character for value labels (13px bold Open Sans, en-US grouping). */
const VALUE_CHAR_PX = 8;
const valueTextRightPad = 6;

const svgW = computed(() => Math.max(240, measuredW.value));

const rowsFromServer = computed(() => {
  const s = props.rowsJson;
  if (typeof s !== "string" || !s.trim()) return undefined;
  try {
    const data = JSON.parse(s);
    return Array.isArray(data) ? data : undefined;
  } catch {
    return undefined;
  }
});

const resolvedRows = computed(() => {
  if (rowsFromServer.value !== undefined) return rowsFromServer.value;
  if (Array.isArray(props.rows) && props.rows.length > 0) return props.rows;
  return undefined;
});

const hasChartData = computed(
  () =>
    Array.isArray(rowsFromServer.value) &&
    rowsFromServer.value.some((row) => Number(row.count) > 0),
);

const { downloadImage, isDownloading, canDownload } = useChartImageDownload({
  getRootElement: () => wrapRef.value,
  title: () => props.title,
  downloadContext: () => props.downloadContext,
  hasData: hasChartData,
});

const sortedRows = computed(() => {
  const list = resolvedRows.value ?? MOCK_ROWS;
  return [...list].sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0));
});

const svgH = computed(() => {
  const n = sortedRows.value.length;
  return margin.top + Math.max(n, 1) * rowHeight + margin.bottom;
});

const formatCount = (n) =>
  typeof n === "number" && Number.isFinite(n) ? Math.round(n).toLocaleString("en-US") : "—";

const layout = computed(() => {
  const w = svgW.value;
  const list = sortedRows.value;
  const longestLabelLen =
    list.length > 0
      ? list.reduce((max, d) => Math.max(max, String(d.label ?? "").length), 0)
      : 0;
  const labelColW = Math.min(
    160,
    Math.max(24, longestLabelLen * LABEL_CHAR_PX + labelColumnPaddingPx),
  );
  const barStart = margin.left + labelColW + gapLabelToBar;

  const widestFormatted =
    list.length > 0
      ? list.reduce((widest, d) => {
          const s = formatCount(Number(d.count) || 0);
          return s.length > widest.length ? s : widest;
        }, "")
      : formatCount(0);
  const valueColumnReserve = Math.ceil(
    widestFormatted.length * VALUE_CHAR_PX + gapBarToValue + valueTextRightPad,
  );

  const barMaxW = Math.max(0, w - margin.right - barStart - valueColumnReserve);
  const maxCount =
    list.length > 0 ? Math.max(...list.map((d) => Number(d.count) || 0), 1) : 1;
  const labelX = margin.left + labelColW / 2;

  const rows = list.map((d, i) => {
    const count = Number(d.count) || 0;
    const label = d.label != null ? String(d.label) : "";
    const cy = margin.top + rowHeight * i + rowHeight / 2;
    const barW = maxCount > 0 ? (count / maxCount) * barMaxW : 0;
    const barX = barStart;
    const barY = cy - barHeight / 2;
    const valueX = count > 0 ? barStart + barW + gapBarToValue : barStart + minBarWidth + gapBarToValue;
    const barPath =
      count > 0 && barW > 0
        ? horizontalBarPathRightRound(barX, barY, barW, barHeight, barRightCornerRadius)
        : "";

    return {
      label,
      count,
      cy,
      labelX,
      barX,
      barY,
      barWidth: Math.max(barW, 0),
      barHeight,
      barPath,
      valueX,
    };
  });

  return { rows };
});

const ariaLabel = computed(() => {
  const parts = layout.value.rows.map((r) => `${r.label}: ${r.count}`).join("; ");
  return `${props.title}. ${parts}`;
});

let ro;

onMounted(() => {
  const measure = () => {
    const cw = chartRef.value?.clientWidth;
    const ww = wrapRef.value?.clientWidth;
    const w = cw && cw > 0 ? cw : ww && ww > 0 ? ww : 280;
    measuredW.value = w;
  };
  nextTick(() => {
    measure();
    ro = new ResizeObserver(measure);
    if (chartRef.value) ro.observe(chartRef.value);
  });
});

onBeforeUnmount(() => ro?.disconnect());
</script>

<style scoped>
.buyer-country-bars__title {
  margin: 0;
  color: #111827;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.buyer-country-bars__subtitle {
  margin: 8px 0 0;
  color: #9ca3af;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.buyer-country-bars__chart {
  min-width: 0;
}

.buyer-country-bars__svg {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  margin-left: 0;
  margin-right: 0;
}

.buyer-country-bars__row-label {
  fill: #6b7280;
  font-family: "Open Sans", sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: normal;
}

.buyer-country-bars__value {
  fill: #111827;
  font-family: "Open Sans", sans-serif;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: normal;
}
</style>
